import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from '@savvy-kit/components/combobox';
import { Briefcase, Mail, Megaphone, Palette } from 'lucide-react';

import { formatFileName } from './helpers';

export type SearchableFile = {
  readonly id: string | number;
  readonly name: string | null;
  readonly tags: readonly (string | null)[] | null;
  readonly parentPath: string | null;
  readonly category: string;
};

export type AssetCounts = {
  resources: number;
  emailTemplates: number;
  socialMedia: number;
  brandMaterials: number;
};

const assetCategories = [
  {
    title: 'Resources',
    description: 'Decks, one-pagers, and guides',
    icon: Briefcase,
    iconBg: 'bg-green-50 dark:bg-muted text-green-500 dark:text-green-300',
    assetType: 'resources',
    countKey: 'resources' as const,
  },
  {
    title: 'Email Templates',
    description: 'Ready-to-send client and prospect emails',
    icon: Mail,
    iconBg: 'bg-cyan-50 dark:bg-muted text-cyan-500 dark:text-cyan-300',
    assetType: 'emailTemplates',
    countKey: 'emailTemplates' as const,
  },
  {
    title: 'Social Media',
    description: 'Posts for LinkedIn, X, and more',
    icon: Megaphone,
    iconBg: 'bg-stone-100 dark:bg-muted text-stone-500 dark:text-stone-300',
    assetType: 'socialMediaPosts',
    countKey: 'socialMedia' as const,
  },
  {
    title: 'Brand Materials',
    description: 'Logos, banners, and backgrounds',
    icon: Palette,
    iconBg: 'bg-purple-50 dark:bg-muted text-purple-500 dark:text-purple-300',
    assetType: 'brandMaterials',
    countKey: 'brandMaterials' as const,
  },
];

export function MarketingSearch({ searchableFiles = [] }: { readonly searchableFiles?: SearchableFile[] }) {
  const router = useRouter();
  const [search, setSearch] = useState('');

  const filteredFiles = useMemo(() => {
    if (!search) return [];
    const searchLower = search.toLowerCase();
    return searchableFiles
      .filter(
        (file) =>
          file.name?.toLowerCase().replaceAll('-', ' ').includes(searchLower) ||
          file.tags?.some((tag) => tag?.toLowerCase().includes(searchLower)),
      )
      .slice(0, 20);
  }, [search, searchableFiles]);

  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchableFile[]> = {};
    for (const file of filteredFiles) {
      const existing = groups[file.category];
      if (existing) {
        existing.push(file);
      } else {
        groups[file.category] = [file];
      }
    }
    return groups;
  }, [filteredFiles]);

  const categoryKeys = Object.keys(groupedResults);

  return (
    <Combobox
      value={null}
      onValueChange={(fileId: string | null) => {
        if (fileId) {
          router.push(`/advisor/dashboard/marketing/asset/${fileId}`);
          setSearch('');
        }
      }}
      onInputValueChange={setSearch}
      inputValue={search}
      open={search.length > 0}
    >
      <ComboboxInput
        className="bg-white"
        placeholder="Search for resources, emails, social posts, brand materials…"
        showTrigger={false}
        showClear={search.length > 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && search.trim()) {
            // If no highlighted item in the list, navigate to discovery with the search term
            const highlighted = document.querySelector('[data-slot="combobox-item"][data-highlighted]');
            if (!highlighted) {
              e.preventDefault();
              router.push(`/advisor/dashboard/marketing/discovery?search=${encodeURIComponent(search.trim())}`);
              setSearch('');
            }
          }
        }}
      />
      <ComboboxContent>
        <ComboboxList>
          {search.trim() && (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-secondary hover:bg-component-hover cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  router.push(`/advisor/dashboard/marketing/discovery?search=${encodeURIComponent(search.trim())}`);
                  setSearch('');
                }}
              >
                Search all for &ldquo;{search.trim()}&rdquo; &rarr;
              </button>
              <ComboboxSeparator />
            </>
          )}
          {categoryKeys.map((category, i) => (
            <div key={category}>
              {i > 0 && <ComboboxSeparator />}
              <ComboboxGroup>
                <ComboboxLabel>{category}</ComboboxLabel>
                {(groupedResults[category] ?? []).map((file) => {
                  const pathLower = file.parentPath?.toLowerCase() ?? '';
                  const audienceLabel = (() => {
                    if (pathLower.includes('client facing')) {
                      return 'Client facing';
                    }

                    if (pathLower.includes('advisor facing')) {
                      return 'Advisor facing';
                    }

                    return null;
                  })();
                  return (
                    <ComboboxItem key={file.id} value={file.id.toString()}>
                      <span className="truncate">{formatFileName(file.name || '')}</span>
                      {audienceLabel && (
                        <span className="shrink-0 ml-auto text-[10px] font-medium text-secondary bg-stone-100 dark:bg-muted rounded-full px-1.5 py-0.5">
                          {audienceLabel}
                        </span>
                      )}
                    </ComboboxItem>
                  );
                })}
              </ComboboxGroup>
            </div>
          ))}
          {filteredFiles.length === 0 && (
            <div className="flex w-full justify-center py-2 text-center text-sm text-muted-foreground">
              No assets found
            </div>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export function MarketingBrowseAssets({ counts }: { readonly counts?: AssetCounts }) {
  const router = useRouter();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {assetCategories.map((category, i) => (
        <button
          key={category.assetType}
          type="button"
          className="group relative block bg-card border border-light rounded-md p-5 text-left hover:bg-component-hover hover:-translate-y-0.5 hover:shadow-lg transition-all duration-150 cursor-pointer animate-fade-in-up"
          style={{ animationDelay: `${i * 75}ms` }}
          onClick={() => router.push(`/advisor/dashboard/marketing/discovery?assetType=${category.assetType}`)}
        >
          <div className="flex items-start justify-between mb-4">
            <div className={`size-11 rounded-[10px] flex items-center justify-center ${category.iconBg}`}>
              <category.icon className="size-5" />
            </div>
            {counts && (
              <span className="font-display text-3xl text-slate-400 dark:text-slate-300 tabular-nums leading-none">
                {counts[category.countKey]}
              </span>
            )}
          </div>
          <h4 className="text-[15px] font-semibold text-foreground leading-tight">{category.title}</h4>
          <p className="text-xs text-secondary mt-1 leading-snug">{category.description}</p>
        </button>
      ))}
    </div>
  );
}
